import React, { useMemo, useState } from "react";
import "./AdminPanel.css";

import NavAdmin from "./components/NavAdmin";
import FooterConfig from "./components/FooterConfig";
import CategoryConfig from "./components/CategoryConfig";
import ContactConfig from "./components/ContactConfig";
import AboutUsConfig from "./components/AboutUsConfig";
import HomePageConfig from "./components/HomePageConfig";
import CardConfig from "./components/CardConfig";
import MetaConfig from "./components/MetaConfig";
import PopupConfig from "./components/PopupConfig";
import FormConfig from "./components/FormConfig";
import FormSubmissions from "./components/FormSubmissions";
import PdfConfig from "./components/PdfConfig";

const TABS = [
  {
    key: "metaconfig",
    label: "Meta",
    title: "Global SEO & Metadata",
    description: "Manage site-level title, description, and metadata settings.",
    group: "Core Setup",
  },
  {
    key: "navadmin",
    label: "Navigation",
    title: "Header & Navigation",
    description: "Configure logo, menu links, homepage anchor links, and nav buttons.",
    group: "Core Setup",
  },
  {
    key: "homepageconfig",
    label: "Homepage",
    title: "Homepage Layout & Sections",
    description: "Control content, order, colors, typography, and section behavior.",
    group: "Website Content",
  },
  {
    key: "categoryconfig",
    label: "Gallery",
    title: "Category & Gallery Data",
    description: "Manage categories, subcategories, cards, images, and display flags.",
    group: "Website Content",
  },
  {
    key: "aboutusconfig",
    label: "About",
    title: "About Page Content",
    description: "Edit About page messaging, text blocks, and brand story details.",
    group: "Website Content",
  },
  {
    key: "contactconfig",
    label: "Contact",
    title: "Contact Page Settings",
    description: "Update contact details, map, hero images, and form selection.",
    group: "Conversion",
  },
  {
    key: "pdfconfig",
    label: "PDF",
    title: "PDF Upload & Placement",
    description:
      "Upload PDF files and choose where to show them (home, contact, or a dedicated page).",
    group: "Website Content",
  },
  {
    key: "formconfig",
    label: "Forms",
    title: "Form Builder",
    description: "Create and manage reusable form configurations across the site.",
    group: "Conversion",
  },
  {
    key: "formsubmissions",
    label: "Submissions",
    title: "Form Submissions",
    description:
      "Review submitted form data, filter by date/time, export to Excel (CSV), and delete records.",
    group: "Conversion",
  },
  {
    key: "popupconfig",
    label: "Popup",
    title: "Popup Campaigns",
    description: "Control popup appearance, timing triggers, and lead capture behavior.",
    group: "Conversion",
  },
  {
    key: "footerconfig",
    label: "Footer",
    title: "Footer Settings",
    description: "Configure footer layout, form integration, social visibility, and theme.",
    group: "Branding",
  },
  {
    key: "cardconfig",
    label: "Cards",
    title: "Card Styling Presets",
    description: "Tune card defaults and visual styles used across sections.",
    group: "Branding",
  },
];

const TAB_COMPONENTS = {
  metaconfig: MetaConfig,
  navadmin: NavAdmin,
  categoryconfig: CategoryConfig,
  footerconfig: FooterConfig,
  contactconfig: ContactConfig,
  aboutusconfig: AboutUsConfig,
  homepageconfig: HomePageConfig,
  cardconfig: CardConfig,
  popupconfig: PopupConfig,
  formconfig: FormConfig,
  formsubmissions: FormSubmissions,
  pdfconfig: PdfConfig,
};

const GROUP_ORDER = ["Core Setup", "Website Content", "Conversion", "Branding"];

export default function AdminPanel() {
  const [active, setActive] = useState("navadmin");
  const [search, setSearch] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const filteredTabs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return TABS;

    return TABS.filter((tab) =>
      [
        tab.label,
        tab.title,
        tab.description,
        tab.group,
        tab.key,
      ].some((value) => String(value).toLowerCase().includes(q))
    );
  }, [search]);

  const tabsByGroup = useMemo(() => {
    const groups = GROUP_ORDER.map((group) => ({
      group,
      tabs: filteredTabs.filter((tab) => tab.group === group),
    }));

    return groups.filter((item) => item.tabs.length > 0);
  }, [filteredTabs]);

  const activeTab = TABS.find((tab) => tab.key === active) || TABS[0];
  const ActivePanel = TAB_COMPONENTS[activeTab.key];

  const handleSelectTab = (key) => {
    setActive(key);
    setMobileMenuOpen(false);
  };

  return (
    <div className="adminui-shell">
      <header className="adminui-topbar">
        <div className="adminui-topbar-left">
          <p className="adminui-kicker">Regency Interior</p>
          <h1 className="adminui-title">Admin Control Panel</h1>
        </div>

        <div className="adminui-topbar-right">
          <label htmlFor="admin-search" className="adminui-visually-hidden">
            Search admin sections
          </label>
          <input
            id="admin-search"
            type="text"
            className="adminui-search"
            placeholder="Search sections..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <a
            className="adminui-link-btn"
            href="/"
            target="_blank"
            rel="noreferrer"
          >
            Open Website
          </a>

          <button
            type="button"
            className="adminui-mobile-toggle"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-expanded={mobileMenuOpen}
            aria-controls="admin-sidebar"
          >
            {mobileMenuOpen ? "Close Menu" : "Open Menu"}
          </button>
        </div>
      </header>

      <div className="adminui-body">
        <aside
          id="admin-sidebar"
          className={`adminui-sidebar ${mobileMenuOpen ? "is-open" : ""}`}
        >
          <div className="adminui-sidebar-top">
            <h2>Sections</h2>
            <p>{filteredTabs.length} available</p>
          </div>

          {tabsByGroup.map((groupItem) => (
            <section key={groupItem.group} className="adminui-group">
              <p className="adminui-group-title">{groupItem.group}</p>
              <div className="adminui-group-list">
                {groupItem.tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`adminui-tab ${
                      active === tab.key ? "is-active" : ""
                    }`}
                    onClick={() => handleSelectTab(tab.key)}
                  >
                    <span className="adminui-tab-label">{tab.label}</span>
                    <span className="adminui-tab-desc">{tab.description}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}

          {!tabsByGroup.length && (
            <div className="adminui-empty">
              No section matches your search. Try another keyword.
            </div>
          )}
        </aside>

        <main className="adminui-main">
          <div className="adminui-context">
            <p className="adminui-context-group">{activeTab.group}</p>
            <h2>{activeTab.title}</h2>
            <p>{activeTab.description}</p>
          </div>

          <section className="adminui-section">
            {ActivePanel ? (
              <ActivePanel />
            ) : (
              <div className="adminui-empty">
                Selected section is not available.
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
